import { useEffect } from 'react';
import { Helmet } from 'react-helmet';

export default function EventsUp() {
  useEffect(() => {
    // Load all external stylesheets
    const stylesheets = [
      '/eventsup-assets/css/bootstrap.min.css',
      '/eventsup-assets/css/font-awesome.min.css',
      '/eventsup-assets/css/owl.carousel.min.css',
      '/eventsup-assets/css/owl.theme.default.min.css',
      '/eventsup-assets/css/stellarnav.css',
      '/eventsup-assets/css/aos.css',
      '/eventsup-assets/css/style.css',
      '/eventsup-assets/css/responsive.css',
    ];

    stylesheets.forEach(href => {
      if (!document.querySelector(`link[href="${href}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        document.head.appendChild(link);
      }
    });

    // Load all external scripts in sequence
    const scripts = [
      '/eventsup-assets/js/jquery.min.js',
      '/eventsup-assets/js/bootstrap.bundle.min.js',
      '/eventsup-assets/js/owl.carousel.min.js',
      '/eventsup-assets/js/aos.js',
      '/eventsup-assets/js/stellarnav.js',
      '/eventsup-assets/js/custom.js',
    ];

    let scriptIndex = 0;

    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }
        
        const script = document.createElement('script');
        script.src = src;
        script.async = false;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    };

    const loadScriptsSequentially = async () => {
      for (const src of scripts) {
        try {
          await loadScript(src);
        } catch (error) {
          console.error(`Failed to load script: ${src}`, error);
        }
      }
      
      // Initialize the page after all scripts are loaded
      setTimeout(() => {
        if (window.initEventPage) {
          window.initEventPage();
        }
      }, 500);
    };

    loadScriptsSequentially();

    // Cleanup function
    return () => {
      // Optional: remove scripts and stylesheets on unmount if needed
    };
  }, []);

  return (
    <div className="eventsup-root" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <Helmet>
        <title>EventsUp - The Complete Event Management Suite</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
        <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>

      {/* Custom EventsUp Header */}
      <header style={{ 
        backgroundColor: '#fff', 
        borderBottom: '1px solid #e5e5e5',
        padding: '20px 0',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000
      }}>
        <div className="container" style={{ maxWidth: '1140px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              {/* Technofocus logo (PNG extracted from original inline SVG to avoid JSX namespaced-attribute issues) */}
              <img src="/technofocus-header-logo.png" alt="Technofocus" style={{ height: '20px', width: 'auto', display: 'block' }} />
              <span style={{ color: '#ccc', fontSize: '24px', fontWeight: '300', lineHeight: 1 }}>|</span>
              <img src="/eventsup-header-logo.svg" alt="EventsUp" style={{ height: '20px', width: 'auto', display: 'block', marginTop: '4px' }} />
            </div>
            <a href="https://technofocus.ai/about" target="_blank" rel="noopener noreferrer" style={{ 
              fontFamily: 'Lexend, sans-serif',
              fontSize: '15px',
              color: '#252526',
              textDecoration: 'none',
              fontWeight: 400
            }} data-testid="header-about-link">About Us</a>
          </div>
        </div>
      </header>

      <div className="eventsup-page-wrapper" style={{ marginTop: '80px' }}>
        <section className="hero-section">
          <div className="container" style={{ maxWidth: '1140px', margin: '0 auto' }}>
            <div className="row align-items-center justify-content-center">
              <div className="col-lg-6 col-md-6" style={{ textAlign: 'left' }}>
                <div className="content-sec">
                  <div className="evnt-logo" style={{ marginBottom: '30px' }}>
                    <img src="/eventsup-logo.png" alt="EventsUp Logo" style={{ maxWidth: '280px' }} />
                  </div>
                  <h1 style={{ fontSize: '42px', lineHeight: '52px', marginBottom: '20px' }}>The Complete Event Management Suite</h1>
                  <p style={{ fontSize: '20px', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 0 }}>
                    Powered by <span style={{ display: 'inline-flex', alignItems: 'center' }}><img src="/eventsup-assets/images/cvnt.svg?v=2" alt="Cvent" style={{ height: '24px' }} /></span>
                  </p>
                </div>
              </div>

              <div className="col-lg-6 col-md-6" style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <img src="/evnt-bnr.png" alt="Event Banner" className="img-fluid" style={{ maxWidth: '100%', height: 'auto' }} />
              </div>
            </div>
          </div>
        </section>

        <section className="partner-slider" id="partner-sec" style={{ padding: '40px 0', backgroundColor: '#fff', overflow: 'hidden' }}>
          <div className="container" style={{ maxWidth: '1140px', margin: '0 auto' }}>
            <div className="relative" style={{ overflow: 'hidden' }}>
              <div className="flex gap-16 items-center animate-marquee" style={{ width: 'max-content' }}>
                {[1, 2, 4, 5, 6, 1, 2, 4, 5, 6, 1, 2, 4, 5, 6].map((n, i) => {
                  const isAmdocs = n === 2;
                  return (
                    <img
                      key={i}
                      src={isAmdocs ? '/logo-amdocs.png' : `/eventsup-assets/partners/partner-${n}.png`}
                      alt="Partner"
                      style={{ height: '40px', width: 'auto', flexShrink: 0 }}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="eventsup-sec" id="abt">
          <div className="container" style={{ maxWidth: '1140px', margin: '0 auto' }}>
            <div className="row">
              <div className="col-lg-5 col-md-5">
                <div className="img-box">
                  <img src="/eventsup-assets/images/abt-eventsUp.svg?v=2" alt="About EventsUp" />
                </div>
              </div>
              <div className="col-lg-7 col-md-7">
                <div className="top-title">
                  <h5>About EventsUp</h5>
                  <h3>Your All-in-One Event Management Solution</h3>
                </div>
                <p>
                  Planning and managing events can be overwhelming, with countless tasks demanding attention—from
                  attendee registration to seamless execution. That's where EventsUp comes in.
                  Whether you're hosting a live event, IT training, conference, or webinar, we provide a fully
                  managed, end-to-end event solution tailored to your unique needs. Our platform simplifies the
                  entire process, allowing you to focus on delivering an exceptional experience while we handle
                  the logistics.
                </p>

                <div className="btm-btn mt-5">
                  <a href="#explr-evntsup" className="thm-btn">Explore EventsUp</a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="platfrm-sec">
          <div className="container" style={{ maxWidth: '1140px', margin: '0 auto' }}>
            <div className="row">
              <div className="col-lg-7 col-md-7">
                <div className="top-title">
                  <h5>Why our platform</h5>
                  <h3>Why Choose EventsUp?</h3>
                </div>
                <ul>
                  <li><span>Custom Event Design -</span> We tailor every event to match your specific requirements.</li>
                  <li><span>Seamless Registration & Website Creation - </span> From building dedicated event pages to managing registrations, we ensure a hassle-free experience.</li>
                  <li><span>Automated Communication - </span> Transactional emails and reminders keep your attendees informed and engaged.</li>
                  <li><span>Virtual & On-Demand Hosting -</span> Whether live or recorded, we provide robust virtual event solutions.</li>
                  <li><span>Attendance Tracking & Reporting -</span> Gain valuable insights with real-time attendance tracking and comprehensive post-event reports.</li>
                </ul>
                <p>
                  At EventsUp, we take care of the details so you can focus on delivering impactful experiences.
                  Let us manage the complexities while you enjoy a seamless and successful event. Let's elevate
                  your events—effortlessly.
                </p>
              </div>

              <div className="col-lg-5 col-md-5">
                <div className="img-box">
                  <img src="/eventsup-assets/images/platfrm.svg?v=2" alt="Platform" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Experience Section with Tabs */}
        <section className="experience-sec" id="explr-evntsup">
          <div className="container" style={{ maxWidth: '1140px', margin: '0 auto' }}>
            <div className="top-title">
              <h5>EventsUp User Experience highlights</h5>
              <h3>Experience Seamless & Intuitive Events</h3>
            </div>

            <ul className="nav nav-tabs">
              <li className="nav-item">
                <a className="nav-link active" data-bs-toggle="tab" href="#attendee">Attendee Experience</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" data-bs-toggle="tab" href="#moderator">Moderator & Speaker Experience</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" data-bs-toggle="tab" href="#stakeholder">Stakeholder Experience</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" data-bs-toggle="tab" href="#features">Additional Features</a>
              </li>
            </ul>

            {/* Tab Content */}
            <div className="tab-content">
              {/* Attendee Experience Tab */}
              <div className="tab-pane container active" id="attendee">
                <div className="inner-tab">
                  <div className="inner-content-sec">
                    <div className="nav flex-column nav-pills" id="v-pills-tab" role="tablist" aria-orientation="vertical">
                      <button className="nav-link active btncheck" id="v-pills-moderator-tab" data-bs-toggle="pill" data-bs-target="#v-pills-moderator" type="button" role="tab" aria-controls="v-pills-moderator" aria-selected="true">Customized Event Website</button>

                      <button className="nav-link btncheck" id="v-pills-Speaker-tab" data-bs-toggle="pill" data-bs-target="#v-pills-speaker" type="button" role="tab" aria-controls="v-pills-speaker" aria-selected="false">Registration Process</button>

                      {/* Accordion 1 */}
                      <div className="accordion acc-btn-sec" id="sessionAccordion">
                        <div className="accordion-item">
                          <h2 className="accordion-header" id="headingSession">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseSession" aria-expanded="false" aria-controls="collapseSession">
                              Session Selection & Submission
                            </button>
                          </h2>
                          <div id="collapseSession" className="accordion-collapse collapse addShow" aria-labelledby="headingSession" data-bs-parent="#sessionAccordion">
                            <div className="accordion-body">
                              <ul className="nav flex-column nav-pills" id="v-pills-tab" role="tablist">
                                <li className="nav-item">
                                  <a className="nav-link" id="v-pills-selection-tab" data-bs-toggle="pill" href="#v-pills-selection" role="tab" aria-controls="v-pills-selection" aria-selected="false">Session Selection</a>
                                </li>
                                <li className="nav-item">
                                  <a className="nav-link" id="v-pills-submission-tab" data-bs-toggle="pill" href="#v-pills-submission" role="tab" aria-controls="v-pills-submission" aria-selected="false">Registration Submission</a>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Accordion 2 */}
                      <div className="accordion acc-btn-sec" id="emailAccordion">
                        <div className="accordion-item">
                          <h2 className="accordion-header" id="headingEmail">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseEmail" aria-expanded="false" aria-controls="collapseEmail">
                              Email Communication
                            </button>
                          </h2>
                          <div id="collapseEmail" className="accordion-collapse collapse" aria-labelledby="headingEmail" data-bs-parent="#emailAccordion">
                            <div className="accordion-body">
                              <ul className="nav flex-column nav-pills" id="v-pills-tab2" role="tablist">
                                <li className="nav-item">
                                  <a className="nav-link" id="v-pills-preevent-tab" data-bs-toggle="pill" href="#v-pills-preevent" role="tab" aria-controls="v-pills-preevent" aria-selected="false">Pre-Event Emails</a>
                                </li>
                                <li className="nav-item">
                                  <a className="nav-link" id="v-pills-postevent-tab" data-bs-toggle="pill" href="#v-pills-postevent" role="tab" aria-controls="v-pills-postevent" aria-selected="false">Post-Event Emails</a>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Accordion 3 */}
                      <div className="accordion acc-btn-sec" id="eventsupAccordion">
                        <div className="accordion-item">
                          <h2 className="accordion-header" id="headingEventsUp">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseEventsUp" aria-expanded="false" aria-controls="collapseEventsUp">
                              EventsUp Virtual Platform
                            </button>
                          </h2>
                          <div id="collapseEventsUp" className="accordion-collapse collapse" aria-labelledby="headingEventsUp" data-bs-parent="#eventsupAccordion">
                            <div className="accordion-body">
                              <ul className="nav flex-column nav-pills" id="v-pills-eventsup-tab" role="tablist">
                                <li className="nav-item">
                                  <a className="nav-link" id="v-pills-platform-tab" data-bs-toggle="pill" href="#v-pills-platform" role="tab" aria-controls="v-pills-platform" aria-selected="false">Platform Overview</a>
                                </li>
                                <li className="nav-item">
                                  <a className="nav-link" id="v-pills-timezone-tab" data-bs-toggle="pill" href="#v-pills-timezone" role="tab" aria-controls="v-pills-timezone" aria-selected="false">Notifications & Time-Zone</a>
                                </li>
                                <li className="nav-item">
                                  <a className="nav-link d-flex justify-content-between align-items-center innrul" data-bs-toggle="collapse" href="#sessionTypesSub" role="button" aria-expanded="false" aria-controls="sessionTypesSub">
                                    Session Types
                                    <span className="side-arw">
                                      <img src="/eventsup-assets/images/right.svg?v=2" alt="arrow" />
                                    </span>
                                  </a>
                                  <div className="collapse ms-3" id="sessionTypesSub">
                                    <ul className="nav flex-column">
                                      <li className="nav-item">
                                        <a className="nav-link" id="v-pills-livestream-tab" data-bs-toggle="pill" href="#v-pills-livestream" role="tab" aria-controls="v-pills-livestream" aria-selected="false">Live-Stream Sessions</a>
                                      </li>
                                      <li className="nav-item">
                                        <a className="nav-link" id="v-pills-collaborative-tab" data-bs-toggle="pill" href="#v-pills-collaborative" role="tab" aria-controls="v-pills-collaborative" aria-selected="false">Collaborative Sessions</a>
                                      </li>
                                    </ul>
                                  </div>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="tab-content" id="v-pills-tabContent">
                      {/* Website Tab */}
                      <div className="tab-pane fade show active" id="v-pills-moderator" role="tabpanel" aria-labelledby="v-pills-moderator-tab">
                        <div className="inner-tab-content">
                          <ul>
                            <li>At EventsUp, we understand your needs and provide you with customized website for your event which works as a landing page for your attendees.</li>
                            <li>Display all the event related information on your website leaving the registration pages for crucial information to be captured from attendees.</li>
                            <li>Enables you to not just promote your event but your organization as we embed your logo, theme, design and any other possibilities you may think of.</li>
                          </ul>

                          <div className="slider-box-sec">
                            <div className="owl-carousel owl-theme owl-img-responsive" id="img-sldr">
                              <div className="item">
                                <img src="/eventsup-assets/images/KYNDRYL Lp.png?v=2" alt="KYNDRYL Landing Page" />
                              </div>
                              <div className="item">
                                <img src="/eventsup-assets/images/Amdocs-LP.png?v=2" alt="Amdocs Landing Page" />
                              </div>
                              <div className="item">
                                <img src="/eventsup-assets/images/CSP reg page.png?v=2" alt="CSP Registration Page" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Registration Process Tab */}
                      <div className="tab-pane fade" id="v-pills-speaker" role="tabpanel" aria-labelledby="v-pills-speaker-tab">
                        <div className="inner-tab-content">
                          <p>
                            We understand the value of data in today's market. Thus, we at EventsUp enable you to not just collect the basic details of an attendee such as their name or email address, but any required information not limited to their company, designation, address, or any event-based requirements that may include their role, zone, skills or any other data you may need on your end.
                          </p>

                          <div className="slider-box-sec">
                            <div className="owl-carousel owl-theme owl-img-responsive" id="img-sldr1">
                              <div className="item">
                                <img src="/eventsup-assets/images/reg-prcs1.png?v=2" alt="Registration Process 1" />
                              </div>
                              <div className="item">
                                <img src="/eventsup-assets/images/reg-prcs2.png?v=2" alt="Registration Process 2" />
                              </div>
                              <div className="item">
                                <img src="/eventsup-assets/images/reg-prcs3.png?v=2" alt="Registration Process 3" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Session Selection Tab */}
                      <div className="tab-pane fade" id="v-pills-selection" role="tabpanel" aria-labelledby="v-pills-selection-tab">
                        <div className="inner-tab-content">
                          <h4>Session Selection</h4>
                          <p>
                            Collecting the name and email address is not enough to run a smooth event. Your invitees need to know the sessions you are hosting and be able to select from the same. Be it a live or an on-demand session, we enable your invitees to make this selection in just a few clicks. Not all sessions are for everyone – want to restrict who gets to view which session and then make a selection? We have got you covered.
                          </p>
                          <p>
                            Running large scale events could be expensive. Want to charge your attendees for the sessions they select? We provide you with a platform to charge fees for the sessions they select, not just fees but early-bird pricing, tax, VAT, discounts, donations, you name it and we have it.
                          </p>

                          <div className="sldr-img-sngle">
                            <img src="/eventsup-assets/images/session selection.png?v=2" alt="Session Selection" />
                          </div>
                        </div>
                      </div>

                      {/* Registration Submission Tab */}
                      <div className="tab-pane fade" id="v-pills-submission" role="tabpanel" aria-labelledby="v-pills-submission-tab">
                        <div className="inner-tab-content">
                          <h4>Final Submission: Confirm Your Registration</h4>
                          <p>
                            You're almost there! The Final Submission Page is the last and most crucial step in completing your event registration. This step ensures that all your details are correct, payments (if applicable) are processed, and your spot at the event is secured.
                          </p>
                          <h6>Why Is This Step Important?</h6>
                          <ul>
                            <li>Accuracy Matters – Double-check your personal information, ticket selection, and any additional preferences to avoid last-minute issues.</li>
                            <li>Secure Your Spot – Finalizing your submission guarantees your place at the event. Incomplete registrations may not be accepted.</li>
                            <li>Receive Confirmation – Upon successful submission, you'll receive a confirmation email with event details, entry passes, and next steps.</li>
                          </ul>

                          <h6>What to Do Before Final Submission?</h6>
                          <ul>
                            <li>Review your information carefully.</li>
                            <li>Confirm your payment details (if applicable).</li>
                            <li>Agree to event terms & conditions.</li>
                          </ul>
                          <p>Once you hit Submit, you're all set! Get ready for an amazing experience, and keep an eye on your inbox for further updates.</p>

                          <div className="sldr-img-sngle">
                            <img src="/eventsup-assets/images/Reg submission.png?v=2" alt="Registration Submission" />
                          </div>
                        </div>
                      </div>

                      {/* Pre-Event Emails Tab */}
                      <div className="tab-pane fade" id="v-pills-preevent" role="tabpanel" aria-labelledby="v-pills-preevent-tab">
                        <div className="inner-tab-content">
                          <h4>Pre-Event Emails</h4>
                          <p>
                            Emails are not just text but it is a way to deliver crucial event related information to an attendee's inbox and no one wants to mess it up. Letting the attendees know what they registered for, instruction on how to attend, virtual links to the sessions and much more, we automate it all for you. With our platform, you can schedule who receives which email and when.
                          </p>
                          <p>
                            It's not just about sending emails, think of an event with 2k+ attendees, how would you track if the attendees received those emails or not? With today's advancement in technology, emails are easily blocked as spam and not even delivered in most of the scenarios. We make sure to track email deliverability for you and put measures in place to place no emails are blocked.
                          </p>
                          <p>
                            Do not just restrict to email deliverability but if the emails were read or not, reporting for each specific email, clicks made through emails, feedback emails, no-show emails, thank you emails and much more.
                          </p>

                          <div className="slider-box-sec">
                            <div className="owl-carousel owl-theme owl-img-responsive" id="img-sldr2">
                              <div className="item">
                                <img src="/eventsup-assets/images/prevnt1.png?v=2" alt="Pre-Event 1" />
                              </div>
                              <div className="item">
                                <img src="/eventsup-assets/images/prevnt2.png?v=2" alt="Pre-Event 2" />
                              </div>
                              <div className="item">
                                <img src="/eventsup-assets/images/prevnt4.png?v=2" alt="Pre-Event 4" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Post-Event Emails Tab */}
                      <div className="tab-pane fade" id="v-pills-postevent" role="tabpanel" aria-labelledby="v-pills-postevent-tab">
                        <div className="inner-tab-content">
                          <h4>Post Event Emails</h4>
                          <p>
                            Post-event emails are essential for maintaining engagement, collecting insights, and maximizing event impact. Automated emails like feedback surveys, no-show follow-ups, and thank-you messages serve key purposes:
                          </p>
                          <ul className="bullt-styl">
                            <li>Gather Feedback – Surveys help improve future events by collecting attendee insights.</li>
                            <li>Re-Engage No-Shows – Follow-up emails offer missed content and encourage future participation.</li>
                            <li>Strengthen Relationships – Thank-you emails show appreciation and enhance attendee experience.</li>
                            <li>Encourage Future Attendance – Keeping in touch increases event retention and engagement.</li>
                            <li>Enhance Brand Trust – Professional follow-ups boost credibility and event ROI.</li>
                          </ul>
                          <p>These emails help create lasting connections, improve event quality, and drive long-term success.</p>

                          <div className="slider-box-sec">
                            <div className="owl-carousel owl-theme owl-img-responsive" id="img-sldr3">
                              <div className="item">
                                <img src="/eventsup-assets/images/pst-evnt1.png?v=2" alt="Post Event 1" />
                              </div>
                              <div className="item">
                                <img src="/eventsup-assets/images/pst-evnt2.png?v=2" alt="Post Event 2" />
                              </div>
                              <div className="item">
                                <img src="/eventsup-assets/images/pst-evnt3.png?v=2" alt="Post Event 3" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Platform Overview Tab */}
                      <div className="tab-pane fade" id="v-pills-platform" role="tabpanel" aria-labelledby="v-pills-platform-tab">
                        <div className="inner-tab-content">
                          <h4>Platform Overview</h4>
                          <p>
                            EventsUp is a robust virtual event platform designed to elevate attendee engagement and streamline the entire event experience. Offering more than just registration and email notifications, EventsUp ensures a secure login process to safeguard attendee data, while delivering a seamless and interactive event journey. With EventsUp, hosting virtual events is not only simple but also secure and engaging for all participants.
                          </p>
                          <p>
                            The Attendee Dashboard serves as the primary interface for participants to engage with the event. It provides an overview of the sessions the attendee is enrolled in, including live, upcoming, and featured sessions. Through the dashboard, attendees can review their registration details, schedule appointments, view notifications, and participate in discussions. Additionally, the dashboard offers easy access to the attendee's profile, allowing for any necessary updates.
                          </p>

                          <div className="slider-box-sec">
                            <div className="owl-carousel owl-theme owl-img-responsive" id="img-sldr4">
                              <div className="item">
                                <img src="/eventsup-assets/images/eventsup-platform overview.png?v=2" alt="Platform Overview" />
                              </div>
                              <div className="item">
                                <img src="/eventsup-assets/images/eventsup-platform overview-1.png?v=2" alt="Platform Overview 1" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Timezone Tab */}
                      <div className="tab-pane fade" id="v-pills-timezone" role="tabpanel" aria-labelledby="v-pills-timezone-tab">
                        <div className="inner-tab-content">
                          <h4>Notifications & Time-Zone</h4>
                          <p>
                            Seamless Time Zone Adaptation – Hosting a global event? Attendees can effortlessly switch between event time zones and their device's local time, ensuring clarity and convenience. Real-Time Push Notifications – Instantly communicate important updates, announcements, and reminders with push notifications sent directly to attendees' screens—no emails required.
                          </p>

                          <div className="sldr-img-sngle">
                            <img src="/eventsup-assets/images/noticiation timezone.png?v=2" alt="Notifications and Timezone" />
                          </div>
                        </div>
                      </div>

                      {/* Live-Stream Sessions Tab */}
                      <div className="tab-pane fade" id="v-pills-livestream" role="tabpanel" aria-labelledby="v-pills-livestream-tab">
                        <div className="inner-tab-content">
                          <h4>Live-Stream Sessions</h4>
                          <p>
                            Enhance your sessions and effectively share information with attendees while maximizing engagement through Q&A, chats, and polls. Whether conducting a live or pre-recorded session, our live-stream setup ensures an immersive experience, allowing attendees to feel as though they are participating in a live event with seamless one-way communication. We provide a platform that enables attendees to join virtually, ensuring they remain fully engaged and connected, all from the comfort of their homes.
                          </p>

                          <div className="slider-box-sec">
                            <div className="owl-carousel owl-theme owl-img-responsive" id="img-sldr5">
                              <div className="item">
                                <img src="/eventsup-assets/images/eventsup-platform-overview.png?v=2" alt="Live Stream 1" />
                              </div>
                              <div className="item">
                                <img src="/eventsup-assets/images/eventsup-platform-overview2.png?v=2" alt="Live Stream 2" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Collaborative Sessions Tab */}
                      <div className="tab-pane fade" id="v-pills-collaborative" role="tabpanel" aria-labelledby="v-pills-collaborative-tab">
                        <div className="inner-tab-content">
                          <h4>Collaborative Sessions</h4>
                          <p>
                            If you're looking to facilitate a session that prioritizes knowledge transfer over mere information sharing, ensuring attendees not only absorb the content but also have the opportunity to clarify any doubts is essential. Our Collaborative Sessions are designed to achieve this by allowing attendees to actively participate, rather than simply observe. Attendees can engage by enabling their audio/video or sharing their screen during the live session. Rest assured, features such as chat, Q&A, and polls remain available to maintain high levels of engagement, even while the speaker delivers the content.
                          </p>

                          <div className="slider-box-sec">
                            <div className="owl-carousel owl-theme owl-img-responsive" id="img-sldr6">
                              <div className="item">
                                <img src="/eventsup-assets/images/collabrt1.png?v=2" alt="Collaborative 1" />
                              </div>
                              <div className="item">
                                <img src="/eventsup-assets/images/collabrt2.png?v=2" alt="Collaborative 2" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Moderator & Speaker Experience Tab */}
              <div className="tab-pane container fade" id="moderator">
                <div className="inner-tab">
                  <div className="inner-content-sec">
                    <div className="nav flex-column nav-pills" id="v-pills-tab" role="tablist" aria-orientation="vertical">
                      <button className="nav-link active" id="v-pills-modjourney-tab" data-bs-toggle="pill" data-bs-target="#v-pills-modjourney" type="button" role="tab" aria-controls="v-pills-modjourney" aria-selected="true">Moderator Role & Journey</button>
                      <button className="nav-link" id="v-pills-speakerjourney-tab" data-bs-toggle="pill" data-bs-target="#v-pills-speakerjourney" type="button" role="tab" aria-controls="v-pills-speakerjourney" aria-selected="false">Speaker Role & Journey</button>
                    </div>

                    <div className="tab-content" id="v-pills-tabContent">
                      {/* Moderator Journey */}
                      <div className="tab-pane fade show active" id="v-pills-modjourney" role="tabpanel" aria-labelledby="v-pills-modjourney-tab">
                        <div className="inner-tab-content">
                          <h4>Moderator Role & Journey</h4>
                          <p>The Moderator is responsible for managing the technical and interactive elements of the session, ensuring the event runs seamlessly from start to finish.</p>

                          <div className="inr-ul-sec">
                            <h6>Before the Event:</h6>
                            <ul>
                              <li>Confirm platform setup and AV functionality</li>
                              <li>Coordinate technical checks with speakers</li>
                              <li>Review session agenda and flow</li>
                            </ul>
                          </div>

                          <div className="inr-ul-sec">
                            <h6>During the Event:</h6>
                            <ul>
                              <li>Manage AV controls and screen sharing</li>
                              <li>Moderate chat, Q&A, and polls</li>
                              <li>Oversee streaming, recording, and timing</li>
                              <li>Address technical issues as they arise</li>
                            </ul>
                          </div>

                          <div className="inr-ul-sec">
                            <h6>After the Event:</h6>
                            <ul>
                              <li>Save and share recordings and logs</li>
                              <li>Provide feedback or a session summary</li>
                            </ul>
                          </div>

                          <div className="slider-box-sec">
                            <div className="owl-carousel owl-theme owl-img-responsive" id="img-sldr7">
                              <div className="item">
                                <img src="/eventsup-assets/images/Moderator Role & Journey  - Image 1.png?v=2" alt="Moderator 1" />
                              </div>
                              <div className="item">
                                <img src="/eventsup-assets/images/Moderator Role & Journey  - Image 2.png?v=2" alt="Moderator 2" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Speaker Journey */}
                      <div className="tab-pane fade" id="v-pills-speakerjourney" role="tabpanel" aria-labelledby="v-pills-speakerjourney-tab">
                        <div className="inner-tab-content">
                          <h4>Speaker Role & Journey</h4>
                          <p>Speakers are responsible for delivering content clearly while keeping the audience engaged and informed throughout the session.</p>

                          <div className="inr-ul-sec">
                            <h6>Before the Event:</h6>
                            <ul>
                              <li>Prepare and organize slides, videos, or any presentation material</li>
                              <li>Coordinate with the moderator for AV checks</li>
                              <li>Test screen-sharing and audio in advance</li>
                            </ul>
                          </div>

                          <div className="inr-ul-sec">
                            <h6>During the Event:</h6>
                            <ul>
                              <li>Deliver content in a structured and engaging manner</li>
                              <li>Interact with the audience through Q&A and comments</li>
                              <li>Help answer technical or content-related questions</li>
                              <li>Collaborate with the moderator for smooth flow and support</li>
                            </ul>
                          </div>

                          <div className="inr-ul-sec">
                            <h6>After the Event:</h6>
                            <ul>
                              <li>Stay for any post-session queries or wrap-up</li>
                              <li>Share materials or follow-up content if needed</li>
                              <li>Gather feedback for future improvements</li>
                            </ul>
                          </div>

                          <div className="sldr-img-sngle">
                            <img src="/eventsup-assets/images/Speaker Role & Journey  - Image 3.png?v=2" alt="Speaker Journey" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stakeholder Experience Tab */}
              <div className="tab-pane container fade" id="stakeholder">
                <div className="inner-tab">
                  <div className="inner-content-sec">
                    <div className="nav flex-column nav-pills" id="v-pills-tab" role="tablist" aria-orientation="vertical">
                      {/* Accordion for Self Service Reporting */}
                      <div className="accordion acc-btn-sec" id="sessionAccordion">
                        <div className="accordion-item">
                          <h2 className="accordion-header" id="headingSession">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseSession2" aria-expanded="false" aria-controls="collapseSession2">
                              Self Service Reporting Portal
                            </button>
                          </h2>
                          <div id="collapseSession2" className="accordion-collapse collapse" aria-labelledby="headingSession" data-bs-parent="#sessionAccordion">
                            <div className="accordion-body">
                              <ul className="nav flex-column nav-pills" id="v-pills-tab" role="tablist">
                                <li className="nav-item">
                                  <a className="nav-link" id="v-pills-securemfa-tab" data-bs-toggle="pill" href="#v-pills-securemfa" role="tab" aria-controls="v-pills-securemfa" aria-selected="false">Secure MFA Login</a>
                                </li>
                                <li className="nav-item">
                                  <a className="nav-link" id="v-pills-portaldash-tab" data-bs-toggle="pill" href="#v-pills-portaldash" role="tab" aria-controls="v-pills-portaldash" aria-selected="false">Reporting Portal Dashboard</a>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>

                      <button className="nav-link btncheck" id="v-pills-stakeholderalrt-tab" data-bs-toggle="pill" data-bs-target="#v-pills-stakeholderalrt" type="button" role="tab" aria-controls="v-pills-stakeholderalrt" aria-selected="false">Alerts for Stakeholder</button>
                    </div>

                    <div className="tab-content" id="v-pills-tabContent">
                      {/* MFA Login */}
                      <div className="tab-pane fade show active" id="v-pills-securemfa" role="tabpanel" aria-labelledby="v-pills-securemfa-tab">
                        <div className="inner-tab-content">
                          <h4>Secure MFA Login</h4>
                          <p>
                            Reporting involves more than just numbers—it often includes sensitive personal data that must be protected. To ensure the highest level of security, we provide you with a secure login to our reporting portal. Access is restricted exclusively to you through a verification code sent directly to your registered email address, safeguarding your information at every step.
                          </p>

                          <div className="slider-box-sec">
                            <div className="owl-carousel owl-theme owl-img-responsive" id="img-sldr8">
                              <div className="item">
                                <img src="/eventsup-assets/images/MFA 1.png?v=2" alt="MFA 1" />
                              </div>
                              <div className="item">
                                <img src="/eventsup-assets/images/MFA 2.png?v=2" alt="MFA 2" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Portal Dashboard */}
                      <div className="tab-pane fade" id="v-pills-portaldash" role="tabpanel" aria-labelledby="v-pills-portaldash-tab">
                        <div className="inner-tab-content">
                          <h4>Reporting Portal Dashboard</h4>
                          <p>
                            Reporting plays a crucial role in every organization, and maintaining accurate, up-to-date reports can be a challenging task. Our Self-Reporting Portal simplifies this process by generating comprehensive reports related to your events—including registrations, cancellations, email communications and much more. We handle the entire reporting process, so you can focus on what matters most.
                          </p>

                          <div className="sldr-img-sngle">
                            <img src="/eventsup-assets/images/Reporting portal access.png?v=2" alt="Reporting Portal" style={{ maxWidth: '90%' }} />
                          </div>
                        </div>
                      </div>

                      {/* Stakeholder Alerts */}
                      <div className="tab-pane fade" id="v-pills-stakeholderalrt" role="tabpanel" aria-labelledby="v-pills-stakeholderalrt-tab">
                        <div className="inner-tab-content">
                          <h4>Alerts for stakeholders</h4>
                          <p>
                            We understand the importance of staying up to date with your event statistics, and we know that running reports can often be time-consuming. To streamline this process, we deliver key registration alerts directly to your inbox. Whether it's notifications about event or session capacity, manual registration approvals, or other important updates, everything can be managed seamlessly via automated emails.
                          </p>

                          <div className="slider-box-sec">
                            <div className="owl-carousel owl-theme owl-img-responsive" id="img-sldr9">
                              <div className="item">
                                <img src="/eventsup-assets/images/Alerts for stakeholders  - Image 1.png?v=2" alt="Alerts 1" />
                              </div>
                              <div className="item">
                                <img src="/eventsup-assets/images/Alerts for stakeholders  - Image 2.png?v=2" alt="Alerts 2" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Features Tab */}
              <div className="tab-pane container fade" id="features">
                <div className="inner-tab">
                  <div className="inner-content-sec">
                    <div className="nav flex-column nav-pills" id="v-pills-tab" role="tablist" aria-orientation="vertical">
                      <button className="nav-link active" id="v-pills-eventapp-tab" data-bs-toggle="pill" data-bs-target="#v-pills-eventapp" type="button" role="tab" aria-controls="v-pills-eventapp" aria-selected="true">Event App</button>
                      <button className="nav-link" id="v-pills-feedbacksurvey-tab" data-bs-toggle="pill" data-bs-target="#v-pills-feedbacksurvey" type="button" role="tab" aria-controls="v-pills-feedbacksurvey" aria-selected="false">Feedback Survey</button>
                    </div>

                    <div className="tab-content" id="v-pills-tabContent">
                      {/* Event App */}
                      <div className="tab-pane fade show active" id="v-pills-eventapp" role="tabpanel" aria-labelledby="v-pills-eventapp-tab">
                        <div className="inner-tab-content">
                          <h4>Event App</h4>
                          <p>
                            Enhance the experience of your in-person attendees by providing continuous engagement through a mobile extension of your event. With a single app, attendees can access essential event information and content at their fingertips—whether it's building a personalized agenda, checking into sessions, receiving push notifications, engaging through chat, Q&A, polls, and surveys, or navigating the event venue. One app, countless possibilities for engagement.
                          </p>

                          <div className="sldr-img-sngle">
                            <img src="/eventsup-assets/images/event-app.png?v=2" alt="Event App" />
                          </div>
                        </div>
                      </div>

                      {/* Feedback Survey */}
                      <div className="tab-pane fade" id="v-pills-feedbacksurvey" role="tabpanel" aria-labelledby="v-pills-feedbacksurvey-tab">
                        <div className="inner-tab-content">
                          <h4>Feedback Survey</h4>
                          <p>
                            Successful events go beyond execution—they're also about understanding attendee experiences to identify what worked well and where there's room for improvement, particularly in content delivery and knowledge transfer. Our platform enables you to seamlessly collect valuable feedback from your attendees, helping you enhance future events.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer style={{ backgroundColor: '#1a1a1a', color: '#fff', padding: '60px 0 25px', borderTop: '1px solid #2a2a2a' }}>
          <div className="container" style={{ maxWidth: '1140px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '60px', alignItems: 'start' }}>
              {/* Left column — brand & description */}
              <div>
                <h3 style={{ fontSize: '24px', fontWeight: '500', marginBottom: '24px', color: '#fff' }}>Technofocus</h3>
                <p style={{ fontSize: '14px', lineHeight: '1.8', color: '#b8b8b8', fontWeight: '300', margin: 0, maxWidth: '460px' }}>
                  Driven by a vision to deliver high-quality technical learning experiences, Technofocus aims to be your trusted partner in mastering Microsoft cloud technologies, empowering both students and professionals alike.
                </p>
              </div>

              {/* Middle column — navigation */}
              <div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  <li>
                    <a href="https://technofocus.ai/about" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'none', fontSize: '15px', fontWeight: '400' }} data-testid="footer-about-link">About Us</a>
                  </li>
                  <li>
                    <a href="https://technofocus.ai/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'none', fontSize: '15px', fontWeight: '400' }} data-testid="footer-privacy-link">Privacy Policy</a>
                  </li>
                  <li>
                    <a href="https://technofocus.ai/eventsup-privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'none', fontSize: '15px', fontWeight: '400' }} data-testid="footer-eventsup-privacy-link">EventsUp Privacy Policy</a>
                  </li>
                </ul>
              </div>

              {/* Right column — contact */}
              <div>
                <a href="mailto:contact@technofocus.ai" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', color: '#fff', textDecoration: 'none', fontSize: '15px' }} data-testid="footer-contact-email">
                  <i className="fa-regular fa-envelope" style={{ fontSize: '18px', color: '#b8b8b8' }} aria-hidden="true"></i>
                  <span>contact@technofocus.ai</span>
                </a>
              </div>
            </div>

            {/* Copyright row */}
            <div style={{ marginTop: '60px', paddingTop: '25px', borderTop: '1px solid #2a2a2a', fontSize: '13px', color: '#888' }}>
              Copyright © 2026 <span style={{ color: '#E91E63', fontWeight: '500' }}>Technofocus</span>
            </div>
          </div>
        </footer>

        {/* Go Top Button */}
        <div className="go-top">
          <i className="fa-solid fa-angle-up" aria-hidden="true"></i>
        </div>
      </div>
    </div>
  );
}
